import { TextField, InputLabel, Typography, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { Controller } from 'react-hook-form';
import { Box, styled, useMediaQuery } from '@mui/system';
import { useContext } from 'react';
import { ThemeContext } from '../../../contexts';
import { inputWrapper } from '../controls.helper';
import { theme } from '../../../utils/theme/godzilla_light';
import InfoIcon from '../../../assets/images/svgs/info.svg'
import { Trans } from 'react-i18next';
import { supportMailUrl } from '../../../constants';

interface IInputProps {
    name: string;
    label: string;
    datatestid?: string;
    type?: string;
    error?: any;
    control: any;
    defaultValue?: string;
    required?: boolean;
    inputProps?: any;
    labelStyles?: any;
    inputStyleProps?: any;
    validDate?: boolean;
    multiline?: boolean;
    rows?: number;
    infoIcon?: boolean;
    sx?: any;
    onInfoClick?: () => void;
    disabled?: boolean;
    infoContent?: string;
    [other: string]: any;
}

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 202,
        backgroundColor: "#1E1E23",
        paddingHorizontal: "8px",
        paddingVertical: "4px",
        color: "#fff",
        fontFamily: "Noto Sans"
    },
});


export function Input(props: IInputProps) {
    const {
        name,
        label,
        datatestid,
        error = null,
        control,
        type = 'text',
        defaultValue,
        required,
        inputProps,
        labelStyles,
        inputStyleProps,
        InputProps,
        multiline,
        rows,
        sx,
        infoIcon,
        onInfoClick,
        disabled,
        infoContent,
        ...other
    } = props;

    const { currentTheme } = useContext(ThemeContext);
    const isMobile = useMediaQuery(theme.breakpoints.down(950))

    const labelClassName = required ? 'required' : '';
    const labelStyle = labelStyles

    return (
        <Box sx={{ ...inputWrapper, ...sx }}>
            <Box sx={{ display: "flex", alignItems: "center", pb: '4px' }}>
                <InputLabel
                    htmlFor={name}
                    className={labelClassName}
                    sx={{
                        ...currentTheme.typography.components.inputLabel,
                        textAlign: "left",
                        '& .MuiFormLabel-asterisk': {
                            color: theme?.palette?.error?.main,
                            position: 'relative',
                            left: isMobile ? '-1px' : '-4px',
                        },
                        whiteSpace: "normal",
                        ...labelStyle,
                    }}
                    required={required}>
                    {label}
                    {infoIcon ?
                        <CustomWidthTooltip
                            title={
                                <Typography sx={{ ...theme.typography.poppinsBodyRegular, color: "#fff", fontWeight: 500, lineHeight: "16px", fontSize: "12px", letterSpacing: "0px" }} >
                                    <Trans
                                        i18nKey={infoContent}
                                        components={{
                                            bold: <strong />,
                                            br: <br />,
                                            // eslint-disable-next-line
                                            a: <a
                                                href={`mailto:${supportMailUrl}`}
                                                style={{ ...theme.typography.poppinsBodyRegular, fontSize: "12px", lineHeight: "16px", color: "#fff", letterSpacing: "0px", textUnderlineOffset: "3px", textDecorationColor: theme.palette.primary.main, textDecorationThickness: "2px" }}></a>
                                        }}
                                    />
                                </Typography>
                            }
                            arrow placement="top">
                            <img src={InfoIcon} alt='tooltip-icon' style={{ marginBottom: "4px", marginLeft: "2px" }} />
                        </CustomWidthTooltip>
                        : null}
                </InputLabel>

            </Box>
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                render={({ field: { ref, ...field } }) => (
                    !disabled ?
                        <TextField
                            variant="outlined"
                            multiline={multiline}
                            rows={rows}
                            fullWidth
                            type={type}
                            inputRef={ref}
                            size="small"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    ...theme.typography.poppinsBodyRegular,
                                    fontSize: "16px",
                                    borderRadius: "4px",
                                    height: "40px",
                                    borderColor: "#404144",
                                    "& fieldset": {
                                        borderColor: "#404144",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#404144",
                                    }
                                },
                                '& .MuiFormHelperText-root': {
                                    ...currentTheme?.typography?.components?.helperText,
                                    mt: '4px',
                                    ml: 0,
                                    mr: 0,
                                    '&.Mui-error': {
                                        color: currentTheme?.palette?.error?.dark,
                                    }
                                }
                            }}
                            {...other}
                            {...field}
                            {...(error && {
                                error: true,
                                helperText: error.message,
                            })}
                        />
                        :
                        <Typography sx={{ ...theme.typography.poppinsBodyRegular, fontSize: "1rem" }}>{defaultValue}</Typography>
                )}
            />
        </Box>
    );
}
